import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DataTableDirective } from 'angular-datatables';
import { catchError, distinctUntilChanged, finalize, map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { helper } from 'src/environments/helper';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DialogService } from 'src/app/services/dialog.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import Swal from 'sweetalert2';
import { DataTablesResponse } from 'src/app/interfaces/data-table-response.interface';
import { ProgramService } from '../program.service';
import { AddSloturiBlocareComponent } from './add-sloturi-blocare/add-sloturi-blocare.component';
import { EditSloturiBlocareComponent } from './edit-sloturi-blocare/edit-sloturi-blocare.component';
import { SlotBlocareUpdate } from '../program.interface';

@Component({
    selector: 'app-sloturi-blocare',
    templateUrl: './sloturi-blocare.component.html',
    styleUrls: ['./sloturi-blocare.component.scss']
})
export class SloturiBlocareComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild(DataTableDirective, { static: false })
    dtElement!: DataTableDirective;
    helper = helper;

    isLoading = false;
    isFilterLoading = false;
    showCard = false;
    activeValue: string = '-1';
    publicValue: string = '-1';

    dtOptions: any = {};
    dtTrigger: Subject<any> = new Subject<any>();
    dtInstance!: DataTables.Api;

    dtLink =
        environment.interop.basePath +
        environment.interop.dgaspc.activitati.nomenclatoare.sloturiBlocare.listDT;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        private dialogRef: MatDialogRef<SloturiBlocareComponent>,
        private http: HttpClient,
        private dialog: MatDialog,
        private dialogService: DialogService,
        private programService: ProgramService,
        public permissionsService: PermissionsService,
    ) { }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    ngOnInit(): void {
        this.renderDT();
    }

    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();
    }

    ngAfterViewInit(): void {
        this.dtTrigger.next('');
    }

    formatDate(date: string): string {
        const [year, month, day] = date.split('-');
        return `${day}.${month}.${year}`;
    }

    renderDT() {
        this.isLoading = true;
        this.dtOptions = {
            pagingType: 'full_numbers',
            responsive: true,
            pageLength: 25,
            searching: false,
            lengthMenu: [10, 25, 50, 100],
            serverSide: true,
            order: [[0, 'asc']],

            ajax: (dataTablesParameters: any, callback: any) => {
                this.isLoading = true;
                dataTablesParameters.program_id = this.data;

                this.http
                    .post<DataTablesResponse>(
                        this.dtLink, // datatable link, we need this
                        dataTablesParameters // parameters for filtering, send here
                    )
                    .subscribe((resp: any) => {
                        this.isLoading = false;

                        callback({
                            recordsTotal: resp.data.recordsTotal,
                            recordsFiltered: resp.data.recordsFiltered,
                            data: resp.data.data,
                        });
                    });
            },
            language: environment.config.romanianLanguageDataTable,
            columns: [
                {
                    title: 'ID',
                    data: 'id',
                },
                {
                    title: 'Dată început',
                    data: 'date_from',
                    render: (data: any) => {
                        return this.formatDate(data);
                    }
                },
                {
                    title: 'Oră început',
                    data: 'time_from',
                },
                {
                    title: 'Dată sfârșit',
                    data: 'date_end',
                    render: (data: any) => {
                        return this.formatDate(data);
                    }
                },
                {
                    title: 'Oră sfârșit',
                    data: 'time_end',
                },
                {
                    title: 'Acțiuni',
                    data: null,
                    orderable: false,
                    className: 'all',
                    render: (data: any, type: any, row: any, met: any) => {
                        return (
                            `
                                    <div class="d-flex align-left justify-content-left">
                                        <button type="button" class="action-btn edit ms-1">
                                            <img class="btn-img" src="/assets/icons/edit.svg" alt="edit"/>
                                        </button>
                                        <button type="button" class="action-btn delete ms-1">
                                            <img class="btn-img" src="/assets/icons/delete.svg" alt="delete"/>
                                        </button>
                                    </div>
                                `
                        );
                    },
                },
            ],
            rowCallback: (row: Node, data: any[] | any) => {
                const lastTd = (row as HTMLElement).querySelector('td:last-child');
                const editIcon = lastTd?.querySelector('.edit') as HTMLElement;
                const deleteIcon = lastTd?.querySelector('.delete') as HTMLElement;

                if (editIcon) {
                    // if (
                    //     !this.permissionsService.hasPermission(
                    //         'auth-module.ghiseu-settings.ghiseu-module.update'
                    //     )
                    // ) {
                    //     editIcon.style.display = 'none';
                    // }
                    editIcon.addEventListener('click', () => {
                        this.openEditDialog(data);
                    });
                }

                if (deleteIcon) {
                    // if (
                    //     !this.permissionsService.hasPermission(
                    //         'auth-module.ghiseu-settings.ghiseu-module.delete'
                    //     )
                    // ) {
                    //     deleteIcon.style.display = 'none';
                    // }
                    deleteIcon.addEventListener('click', () => {
                        const { id } = data;
                        this.deleteProgram(id);
                    });
                }

                return row;
            },
        };
    }

    rerenderDT(resetPaging = false): void {
        this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
            dtInstance.draw(resetPaging);
        });
        this.showCard = false;
    }

    openAddDialog() {
        const dialogRef = this.dialog.open(AddSloturiBlocareComponent, {
            ...this.dialogService.getDialogConfig(),
            data: this.data
        });
        dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
            if (success) {
                this.isLoading = true;
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Slot blocare adăugat cu succes!',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            }
        });
    }

    openEditDialog(data: any) {
        const dialogRef = this.dialog.open(EditSloturiBlocareComponent, {
            ...this.dialogService.getDialogConfig(),
            data
        });

        dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
            if (success) {
                this.isLoading = true;
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Slot blocare actualizat cu succes!',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            }
        });
    }

    deleteProgram(id: number) {
        Swal.fire({
            title: helper.dialogConfig.commonTitles.areYouSure,
            text: 'Nu o să puteți recupera datele odată cu ștergerea lor!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: helper.dialogConfig.colorButtons.standard,
            confirmButtonText: helper.dialogConfig.buttonLabels.confirm,
            cancelButtonText: helper.dialogConfig.buttonLabels.close,
            showLoaderOnConfirm: true,
            reverseButtons: true,
            allowOutsideClick: false,
        }).then((result) => {
            if (result.isConfirmed) {
                // we do this because we need to have some control on activity
                this.deleteAction(id);
            }
        });
    }

    deleteAction(id: number) {
        this.isLoading = true;
        this.programService.deleteSlotBlocare(id).subscribe(
            () => {
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Slot blocare șters cu succes!',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            },
            (err) => {
                let msg;
                try {
                    // try to catch the message from server
                    msg = err.error.errors.message[0];
                } catch (error) {
                    // controll output message
                    msg = helper.dialogConfig.generalMessages.error;
                }

                Swal.fire({
                    title: helper.dialogConfig.headers.errorAtentie,
                    text: msg,
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.retry,
                    showCancelButton: true,
                    cancelButtonText: helper.dialogConfig.buttonLabels.close,
                    reverseButtons: true,
                    allowOutsideClick: false,
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.deleteAction(id);
                    }
                });
            }
        );
    }
}
